import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Instagram } from "lucide-react";
import { SmartImage } from "@/components/SmartImage";
import { resolveImageWithFallback } from "@/lib/asset-map";
import {
  SERIES_BY_SLUG,
  decodePosition,
  MAX_EPISODES_PER_SEASON,
} from "@/lib/series-taxonomy";
import { useLocale } from "@/hooks/use-locale";
import { countCopy, listCopy, localizedSeasonTitle } from "@/lib/list-locales";

type Episode = {
  id?: string;
  slug: string;
  title: string;
  excerpt?: string | null;
  image_url?: string | null;
  position?: number;
  from_instagram?: boolean | null;
};

type Season<T> = { key: string; number: number; title: string; items: T[] };

/**
 * Seasons come from the stored position (season × episode), not from guessing
 * at keywords. A season holding more than 10 episodes is shown as two parts,
 * so a season is always bingeable in one sitting.
 */
function buildSeasons<T extends Episode>(seriesSlug: string, items: T[]): Season<T>[] {
  const def = SERIES_BY_SLUG[seriesSlug];
  if (!def) return [{ key: "all", number: 1, title: "All episodes", items }];

  const bySeason = new Map<number, T[]>();
  const loose: T[] = [];
  for (const item of items) {
    if (typeof item.position !== "number") {
      loose.push(item);
      continue;
    }
    const { seasonIndex } = decodePosition(item.position);
    if (!def.seasons[seasonIndex]) {
      loose.push(item);
      continue;
    }
    const arr = bySeason.get(seasonIndex) ?? [];
    arr.push(item);
    bySeason.set(seasonIndex, arr);
  }

  const out: Season<T>[] = [];
  def.seasons.forEach((season, index) => {
    const list = bySeason.get(index) ?? [];
    if (list.length === 0) return;
    const parts = Math.ceil(list.length / MAX_EPISODES_PER_SEASON);
    for (let p = 0; p < parts; p += 1) {
      const chunk = list.slice(p * MAX_EPISODES_PER_SEASON, (p + 1) * MAX_EPISODES_PER_SEASON);
      out.push({
        key: `${index}-${p}`,
        number: out.length + 1,
        title: parts > 1 ? `${season.title} · Part ${p + 1}` : season.title,
        items: chunk,
      });
    }
  });

  if (loose.length > 0) {
    out.push({ key: "extra", number: out.length + 1, title: "Also in this series", items: loose });
  }

  return out;
}

export function SeriesSeasons<T extends Episode>({
  items,
  visited,
  search,
  seriesSlug,
}: {
  items: T[];
  visited: Set<string>;
  search?: { from: string };
  seriesSlug: string;
}) {
  const seasons = buildSeasons(seriesSlug, items);
  const [lang] = useLocale();
  const copy = listCopy(lang);
  const counts = countCopy(lang);
  const [active, setActive] = useState(0);
  const season = seasons[active] ?? seasons[0];
  if (!season) return null;

  return (
    <div>
      {seasons.length > 1 && (
        <div className="mb-6 flex flex-wrap gap-2">
          {seasons.map((s, i) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setActive(i)}
              className={`rounded-full border px-3.5 py-2 text-[11px] font-bold uppercase tracking-widest transition ${
                i === active
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border text-muted-foreground hover:border-primary hover:text-primary"
              }`}
            >
              <span data-no-translate>S{s.number} · {localizedSeasonTitle(lang, s.title)}</span>
            </button>
          ))}
        </div>
      )}

       <div data-no-translate className="mb-4 text-[11px] font-bold uppercase leading-relaxed tracking-widest text-muted-foreground">
         {localizedSeasonTitle(lang, season.title)} · {counts.episodes(season.items.length)}
      </div>

      <ol className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3">
        {season.items.map((d, i) => {
          const seen = visited.has(d.slug);
          const img = resolveImageWithFallback(d.image_url ?? null, "discovery", d.slug);
          return (
            <li key={d.id ?? d.slug}>
              <Link
                to="/discovery/$slug"
                params={{ slug: d.slug }}
                search={search}
                className="group block h-full overflow-hidden rounded-2xl border border-border bg-card transition hover:border-primary/40"
              >
                <div className="relative aspect-[5/3] overflow-hidden">
                  {img ? (
                    <SmartImage
                      src={img}
                      alt={d.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  ) : (
                    <div className="h-full w-full bg-gradient-curiosity opacity-40" />
                  )}
                  <div className="absolute left-1.5 top-1.5 rounded-full bg-black/70 px-2 py-0.5 text-[9px] font-bold tracking-widest text-white">
                     <span data-no-translate>S{season.number}:E{i + 1}</span>
                  </div>
                  {d.from_instagram && (
                    <div className="absolute right-1.5 top-1.5 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-pink-500 to-orange-500 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white">
                      <Instagram className="h-2.5 w-2.5" />
                    </div>
                  )}
                </div>
                <div className="min-w-0 p-3 md:p-4">
                   <h3 className="break-words font-display text-lg uppercase leading-tight text-foreground transition group-hover:text-primary sm:text-xl">
                    {d.title}
                  </h3>
                   <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {d.excerpt}
                  </p>
                  <div className="mt-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                     <span data-no-translate>{seen ? `${copy.read} ✓` : copy.unread}</span>
                  </div>
                </div>
              </Link>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
